import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { membershipApi } from "../api";

interface Category {
  id: number;
  name: string;
}

export default function MemberCreatePage() {
  const navigate = useNavigate();

  const [mode, setMode] = useState<"application" | "member">("application");
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    companyName: "",
    applicantName: "",
    email: "",
    phoneNumber: "",
    categoryId: "",
    notes: "",
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    const res = await membershipApi.listCategories();
    setCategories(res);
  };

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.companyName || !form.categoryId) {
      alert("Company Name and Category are required");
      return;
    }

    setLoading(true);

    try {
      if (mode === "application") {
        await membershipApi.createApplication({
          companyName: form.companyName,
          applicantName: form.applicantName,
          email: form.email,
          phoneNumber: form.phoneNumber,
          categoryId: Number(form.categoryId),
          message: form.notes,
        });
      } else {
        await membershipApi.createMember({
          companyName: form.companyName,
          categoryId: Number(form.categoryId),
          notes: form.notes,
        });
      }

      navigate("/hr/membership");
    } finally {
      setLoading(false);
    }
  };
  type InputProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
};

function Input({ label, value, onChange, required }: InputProps) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">
        {label} {required && "*"}
      </label>
      <input
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        className="mt-2 w-full rounded-lg border p-3"
      />
    </div>
  );
}
type TextareaProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
};

function Textarea({ label, value, onChange }: TextareaProps) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <textarea
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        className="mt-2 w-full rounded-lg border p-3"
      />
    </div>
  );
}

type SelectOption = {
  label: string;
  value: string;
};

type SelectProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
};

function Select({ label, value, onChange, options }: SelectProps) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <select
        value={value}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          onChange(e.target.value)
        }
        className="mt-2 w-full rounded-lg border p-3"
      >
        <option value="">Select...</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">
          Create Membership / Application
        </h1>

        {/* Mode Switch */}
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => setMode("application")}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              mode === "application"
                ? "bg-emerald-700 text-white"
                : "bg-slate-200"
            }`}
          >
            Application
          </button>

          <button
            onClick={() => setMode("member")}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              mode === "member"
                ? "bg-emerald-700 text-white"
                : "bg-slate-200"
            }`}
          >
            Direct Member
          </button>
        </div>

        {/* Form */}
        <div className="mt-6 grid gap-4">
          <Input
            label="Company Name"
            value={form.companyName}
            onChange={(v) => handleChange("companyName", v)}
            required
          />

          {mode === "application" && (
            <>
              <Input
                label="Applicant Name"
                value={form.applicantName}
                onChange={(v) => handleChange("applicantName", v)}
              />

              <Input
                label="Email"
                value={form.email}
                onChange={(v) => handleChange("email", v)}
              />

              <Input
                label="Phone Number"
                value={form.phoneNumber}
                onChange={(v) => handleChange("phoneNumber", v)}
              />
            </>
          )}

          <Select
            label="Membership Category"
            value={form.categoryId}
            onChange={(v) => handleChange("categoryId", v)}
            options={categories.map((c) => ({
              label: c.name,
              value: c.id.toString(),
            }))}
          />

          <Textarea
            label="Notes / Message"
            value={form.notes}
            onChange={(v) => handleChange("notes", v)}
          />

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="mt-4 rounded-xl bg-emerald-700 px-5 py-3 font-semibold text-white"
          >
            {loading ? "Saving..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Reusable Inputs */

function Input({
  label,
  value,
  onChange,
  required,
}: any) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">
        {label} {required && "*"}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-lg border p-3"
      />
    </div>
  );
}

function Select({ label, value, onChange, options }: any) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-lg border p-3"
      >
        <option value="">Select...</option>
        {options.map((opt: any) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Textarea({ label, value, onChange }: any) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-lg border p-3"
      />
    </div>
  );
}